import { makeAutoObservable, runInAction, toJS, autorun, set } from "mobx";
import Accessor from "./Accessor";
import BrowserX from "./BrowserX";
import States from "__SYSDefault/States";
import _ from "lodash";

class AppStore {

  static __permanent = {
    lang: "EN",
    server: {},
    user: {},
    config: {},
    save: {},
    queuedJobs: [],
    loginMethod: "",
    recoverPath: null,
    darkMode: false,
    persistKeys: [],
    agent: "side"
  };

  static __non_permanent = {
    browser: "",
    innerWidth: null,
    initialized: false,
    loggedIn: null,
    autoLogin: true,
    qsReloading: false,
    regFunc: {},
    regVal: {},
    mini: true,
    currentPageInfo: null
  };

  constructor(){
    makeAutoObservable(this);
    
    let firstRun = true;
    set(this, {
      ...AppStore.__permanent,
      ...AppStore.__non_permanent,
      ...States.__permanent,
      ...States.__non_permanent
    });

    autorun(() => {
      // on load check if there"s an existing STORE on 
      // localStorage and extend the STORE
      if (firstRun) {
        let existingStore = window.localStorage.getItem("GState");
        if (existingStore) {
          set(this, JSON.parse(existingStore));
        }
      }
  
      // from then on serialize and save to localStorage
      let serializedThis = toJS(this);
      let cleanedThis = this.GetCleaned(serializedThis);

      window.localStorage.setItem(
        "GState", 
        JSON.stringify(cleanedThis)
      );
    });
  
    firstRun = false;
  }

  RESET(){
    runInAction(() => {
      set(this, {
        ...AppStore.__permanent,
        ...AppStore.__non_permanent,
        ...States.__permanent,
        ...States.__non_permanent
      });
    });
  }

  GetCleaned(obj){
    let permanentKeys = Object.keys({
      ...AppStore.__permanent,
      ...States.__permanent
    });

    permanentKeys = _.concat(permanentKeys, this.persistKeys ?? []);
    
    let rtn = Object.keys(obj).reduce((acc, key) => {
      if (permanentKeys.includes(key)) {
        acc[key] = obj[key];
      }
      return acc;
    }, {});

    return rtn;
  }

  CLEAN(){
    runInAction(() => {
      set(this, this.GetCleaned(this));
    });
  }

  UPDATE(field, value){
    runInAction(() => {
      Accessor.Set(this, field, value);
    });
  }

  GET(field){
    return toJS(Accessor.Get(this, field));
  }

  setLang(lang){
    this.UPDATE("lang", lang);
  }

  setServer(server){
    this.UPDATE("server", server);
  }

  setBrowser(){
    let browser = BrowserX.getBrowser();
    this.UPDATE("browser", browser);
  }

  setUser(user){
    this.UPDATE("user", user);
  }

  clearUser(){
    this.UPDATE("user", {});
  }

  setConfig(config){
    this.UPDATE("config", config);
  }

  clearConfig(){
    this.UPDATE("config", {});
  }

  setSave(save){
    this.UPDATE("save", save);
  }

  clearSave(){
    this.UPDATE("save", {});
  }

  setLoginMethod(loginMethod){
    this.UPDATE("loginMethod", loginMethod);
  }

  clearLoginMethod(){
    this.UPDATE("loginMethod", "");
  }

  setInitialized(tf){
    this.UPDATE("initialized", tf);
  }

  setLogged(tf){
    this.UPDATE("loggedIn", tf);
  }

  setMini(tf){
    this.UPDATE("mini", tf);
  }

  setDarkMode(tf){
    this.UPDATE("darkMode", tf);
  }

  setViewer(viewer){
    this.UPDATE("viewer", viewer);
  }

  clearViewer(){
    this.UPDATE("viewer", {});
  }

  setAutoLogin(tf){
    this.UPDATE("autoLogin", tf);
  }

  setRecoverPath(url){
    this.UPDATE("recoverPath", url);
  }

  setQsReloading(tf){
    this.UPDATE("qsReloading", tf);
  }

  setInnerWidth(width){
    this.UPDATE("innerWidth", width);
  }

  /**
   * 
   * @param {"none" | "side" | "full"} agent 
   */
  setAgent(agent){
    this.UPDATE("agent", agent);
  }

  miniAgent(){
    if(this.agent !== "none"){
      this.UPDATE("agent", "side");
    }
  }

  clearRecoverPath(){
    this.UPDATE("recoverPath", null);
  }

  registerValue(key, value){
    this.UPDATE("regVal", {...(this.regVal ?? {}), [key]: value});
  }

  getValue(key){
    return toJS(this.regVal[key]);
  }

  clearValue(key){
    let regVal = this.regVal;
    delete regVal[key];
    this.UPDATE("regVal", regVal);
  }

  clearPersistKeys(){
    this.UPDATE("persistKeys", []);
  }

  setCurrentPageInfo(info){
    this.UPDATE("currentPageInfo", info);
  }

  clearCurrentPageInfo(){
    this.UPDATE("currentPageInfo", null);
  }
  
}

const GState = new AppStore();
export default GState;